/* eslint-disable react-refresh/only-export-components */
/**
 * JobAuto Calculator helpers – shared types, constants, and small UI components.
 * Extracted from JobAutoCalculator.tsx for file-size compliance.
 */
import { Info } from 'lucide-react'
import { cn } from '../../shared'

// Jobrad interface
export interface JobradInfo {
  id: string
  name: string          // e.g. "Trek Domane SL5"
  listenpreis: number   // UVP in €
}

// Format currency in German locale
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(value)
}

export function InfoTooltip({ children }: { children: React.ReactNode }) {
  const text = typeof children === 'string' ? children : ''
  return (
    <span className="relative inline-flex items-center group cursor-help shrink-0">
      <Info className="h-4 w-4 text-zinc-400" />
      {text && (
        <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 w-56 rounded-md bg-zinc-800 dark:bg-zinc-700 px-2.5 py-1.5 text-xs text-white shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-75 z-50 text-center leading-snug">
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-zinc-800 dark:border-t-zinc-700" />
        </span>
      )}
    </span>
  )
}

export function ComparisonRow({
  label,
  ohne,
  mit,
  info
}: {
  label: string
  ohne: number
  mit: number
  info?: string
}) {
  const diff = mit - ohne
  const isPositive = diff > 0

  return (
    <div className="grid grid-cols-4 gap-2 py-1 text-sm">
      <span className="text-zinc-500 dark:text-zinc-400 col-span-1">
        {label}
        {info && <InfoTooltip>{info}</InfoTooltip>}
      </span>
      <span className="font-mono text-right text-zinc-900 dark:text-zinc-100">{formatCurrency(ohne)}</span>
      <span className="font-mono text-right text-zinc-900 dark:text-zinc-100">{formatCurrency(mit)}</span>
      <span className={cn("font-mono text-right", isPositive ? 'text-red' : 'text-green')}>
        {diff > 0 ? '+' : ''}{formatCurrency(diff)}
      </span>
    </div>
  )
}

// Reusable input styles
export const inputStyles = "w-full px-3 py-2 border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 rounded focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
export const labelStyles = "flex items-center gap-1 text-sm font-medium text-zinc-700 dark:text-zinc-300"
export const selectStyles = "w-full px-3 py-2 border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 rounded focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
